import ClientSheetUpdateBeneficiaryRequestResource from "common/resources/ClientSheet/ClientSheetUpdateBeneficiaryRequestResource";
import CorporationSheetCreateRequestResource from "common/resources/CorporationSheet/CorporationSheetCreateRequestResource";
import CorporationSheetResponseResource from "common/resources/CorporationSheet/CorporationSheetResponseResource";
import CorporationSheetUpdateRequestResource from "common/resources/CorporationSheet/CorporationSheetUpdateRequestResource";
import { ValidationError } from "common/resources/Resource";
import I18n from "components/materials/I18n";
import PageTemplateCreateClient from "components/PageTemplateCreateClient";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import CorporationService from "services/CorporationService";
import { container } from "tsyringe";

import Beneficiaries from "./Beneficiaries";
import { synchronizeRepresentativeBeneficiary } from "./beneficiaryVersion";
import classes from "./classes.module.scss";
import CorporationInfo, { groupsCorporationInfoCreate, groupsCorporationInfoEdit } from "./CorporationInfo";
import LegalRepresentative, { groupsLegalRepresentative } from "./LegalRepresentative";
import { ECountry } from "common/enums/Country/ECountry";

export enum ESteps {
	CORPORATION_INFO = "CORPORATION_INFO",
	LEGAL_REPRESENTATIVE = "LEGAL_REPRESENTATIVE",
	BENEFICIARIES = "BENEFICIARIES",
}

type ICreateOnBehalfOf = {
	creatorId: string;
	officeId: string;
	onComplete: (corporationSheetId: string) => void;
	onCancel: () => void;
};

type IProps = {
	isEdit?: boolean;
	createOnBehalfOf?: ICreateOnBehalfOf;
};

const corporationService = container.resolve(CorporationService);

export default function CreateCorporation(props: IProps) {
	const { isEdit, createOnBehalfOf } = props;
	const { corporationSheetId } = useParams();
	const { redirectUrl } = useRedirectUrl();
	const { searchParams, setStep, isStep } = useStepNavigation();
	const { completedSteps, formDataCorporationInfo, formDataLegalRepresentative, beneficiaries, errorsCorporationInfo, errorsLegalRepresentative } = useCorporationData(
		isEdit,
		corporationSheetId,
	);
	const steps = useStepDefinitions(isStep, completedSteps, setStep);
	const { subscriptionIdToUpdate } = useCameFromSubscriptionTunnel();

	return (
		<PageTemplateCreateClient
			pageTitle={I18n.asset.pages.subscriptions.create_corporation.page_title}
			headerTitle={I18n.asset.pages.subscriptions.create_corporation.title}
			steps={steps}
			redirectUrl={redirectUrl}
			isEdit={isEdit ? { enabled: isEdit, corporationId: formDataCorporationInfo?.corporationId ?? "" } : undefined}
			hideBackButton={!!createOnBehalfOf}>
			<div className={classes["root"]}>
				{searchParams.get("step") === ESteps.CORPORATION_INFO && <CorporationInfo formData={formDataCorporationInfo} errors={errorsCorporationInfo} isEdit={isEdit} />}
				{searchParams.get("step") === ESteps.LEGAL_REPRESENTATIVE && <LegalRepresentative formData={formDataLegalRepresentative} errors={errorsLegalRepresentative} createOnBehalfOf={createOnBehalfOf} />}
				{searchParams.get("step") === ESteps.BENEFICIARIES && (
					<Beneficiaries
						formDataCorporationInfo={formDataCorporationInfo}
						formDataLegalRepresentative={formDataLegalRepresentative}
						beneficiaries={beneficiaries}
						isEdit={isEdit}
						redirectUrl={redirectUrl}
						subscriptionIdToUpdate={subscriptionIdToUpdate}
						createOnBehalfOf={createOnBehalfOf}
					/>
				)}
			</div>
		</PageTemplateCreateClient>
	);
}

/**
 * @description Hook to check if the user came from the subscription tunnel page.
 * We can't modify the corporation directly in the subscription tunnel page, so we need to check if the user came from there
 * then update the subscription with the new corporation sheet.
 */
export const useCameFromSubscriptionTunnel = () => {
	const location = useLocation();
	const [subscriptionIdToUpdate, setSubscriptionIdToUpdate] = useState<string>("");

	useEffect(() => {
		const state = location.state as { from?: string; subscriptionId?: string } | null;
		if (state?.from === "subscription-tunnel" && state?.subscriptionId) {
			setSubscriptionIdToUpdate(state.subscriptionId);
			// Clear the state to prevent it from being used again
			window.history.replaceState({}, document.title);
		}
	}, [location]);

	return { subscriptionIdToUpdate };
};

const useRedirectUrl = () => {
	const [searchParams] = useSearchParams();
	const [redirectUrl, setRedirectUrl] = useState<string>("");

	useEffect(() => {
		const redirectUrl = searchParams.get("redirectUrl");
		if (redirectUrl) setRedirectUrl(redirectUrl);
	}, [searchParams]);

	return { redirectUrl };
};

const useStepNavigation = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		window.scrollTo(0, 0);
		setSearchParams((prevSearchParams) => {
			const newSearchParams = new URLSearchParams(prevSearchParams);
			const step = newSearchParams.get("step") ?? ESteps.CORPORATION_INFO;
			newSearchParams.set("step", step);
			return newSearchParams;
		});
	}, [setSearchParams]);

	const isStep = useCallback((targetSteps: ESteps[]) => targetSteps.includes(searchParams.get("step") as ESteps), [searchParams]);

	const setStep = useCallback(
		(step: ESteps) => {
			setSearchParams((prevSearchParams) => {
				const newSearchParams = new URLSearchParams(prevSearchParams);
				newSearchParams.set("step", step);
				return newSearchParams;
			});
		},
		[setSearchParams],
	);

	return { searchParams, setSearchParams, setStep, isStep };
};

const useCorporationData = (isEdit: boolean | undefined, corporationSheetId: string | undefined) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [completedSteps, setCompletedSteps] = useState<ESteps[]>([]);
	const [formDataCorporationInfo, setFormDataCorporationInfo] = useState<Partial<CorporationSheetResponseResource> | null>(null);
	const [formDataLegalRepresentative, setFormDataLegalRepresentative] = useState<Partial<CorporationSheetResponseResource> | null>(null);
	const [beneficiaries, setBeneficiaries] = useState<ClientSheetUpdateBeneficiaryRequestResource[]>([]);
	const [errorsCorporationInfo, setErrorsCorporationInfo] = useState<ValidationError[]>([]);
	const [errorsLegalRepresentative, setErrorsLegalRepresentative] = useState<ValidationError[]>([]);

	useEffect(() => {
		if (!isEdit || !corporationSheetId) return;

		const hasAllSteps = searchParams.has(ESteps.CORPORATION_INFO) && searchParams.has(ESteps.LEGAL_REPRESENTATIVE) && searchParams.has(ESteps.BENEFICIARIES);

		if (hasAllSteps) return;

		const fetchData = async () => {
			const corporationSheet = await corporationService.getCorporationSheetById(corporationSheetId);

			setSearchParams((prevSearchParams) => {
				const newSearchParams = new URLSearchParams(prevSearchParams);

				if (!newSearchParams.has(ESteps.CORPORATION_INFO)) {
					const data = JSON.stringify({ formData: hydrateCorporationInfo(corporationSheet, isEdit) });
					newSearchParams.set(ESteps.CORPORATION_INFO, data);
				}

				if (!newSearchParams.has(ESteps.LEGAL_REPRESENTATIVE)) {
					const data = JSON.stringify({ formData: hydrateLegalRepresentative(corporationSheet, isEdit) });
					newSearchParams.set(ESteps.LEGAL_REPRESENTATIVE, data);
				}

				if (!newSearchParams.has(ESteps.BENEFICIARIES)) {
					const data = JSON.stringify({ beneficiaries: corporationSheet.beneficiaries });
					newSearchParams.set(ESteps.BENEFICIARIES, data);
				}

				return newSearchParams;
			});
		};

		fetchData();
	}, [corporationSheetId, isEdit, searchParams, setSearchParams]);

	useEffect(() => {
		const processSteps = async () => {
			const steps: ESteps[] = [];
			setErrorsCorporationInfo([]);
			setErrorsLegalRepresentative([]);

			const paramsCorporationInfo = searchParams.get(ESteps.CORPORATION_INFO);
			const paramsLegalRepresentative = searchParams.get(ESteps.LEGAL_REPRESENTATIVE);
			const paramsBeneficiaries = searchParams.get(ESteps.BENEFICIARIES);
			let representantLegal: ClientSheetUpdateBeneficiaryRequestResource | undefined;

			if (paramsCorporationInfo) {
				try {
					const formDataCorporationInfo = JSON.parse(paramsCorporationInfo).formData;
					const corporationInfoResource = hydrateCorporationInfo(formDataCorporationInfo, isEdit);
					setFormDataCorporationInfo(formDataCorporationInfo);
					await corporationInfoResource.validateOrReject?.({
						groups: isEdit ? groupsCorporationInfoEdit : groupsCorporationInfoCreate,
					});
					steps.push(ESteps.CORPORATION_INFO);
				} catch (error: unknown) {
					console.warn(error);
					if (Array.isArray(error)) setErrorsCorporationInfo(error);
				}
			}

			if (paramsLegalRepresentative) {
				try {
					const formDataLegalRepresentative = JSON.parse(paramsLegalRepresentative).formData;
					const legalRepresentativeResource = hydrateLegalRepresentative(formDataLegalRepresentative, isEdit);
					setFormDataLegalRepresentative(formDataLegalRepresentative);
					await legalRepresentativeResource.validateOrReject?.({ groups: groupsLegalRepresentative });
					representantLegal = legalRepresentativeResource.representantLegal;
					steps.push(ESteps.LEGAL_REPRESENTATIVE);
				} catch (error: unknown) {
					console.warn(error);
					if (Array.isArray(error)) setErrorsLegalRepresentative(error);
				}
			}

			if (paramsBeneficiaries) {
				let beneficiaries: ClientSheetUpdateBeneficiaryRequestResource[] = paramsBeneficiaries ? JSON.parse(paramsBeneficiaries).beneficiaries : [];

				if (representantLegal) {
					beneficiaries = synchronizeRepresentativeBeneficiary(beneficiaries, representantLegal);
				}

				setBeneficiaries(beneficiaries);
				steps.push(ESteps.BENEFICIARIES);
			}

			setCompletedSteps(steps);
		};

		processSteps();
	}, [isEdit, searchParams]);

	return {
		completedSteps,
		formDataCorporationInfo,
		formDataLegalRepresentative,
		beneficiaries,
		errorsCorporationInfo,
		errorsLegalRepresentative,
	};
};

const useStepDefinitions = (isStep: (targetSteps: ESteps[]) => boolean, completedSteps: ESteps[], setStep: (step: ESteps) => void) => {
	return useMemo(
		() => [
			{
				title: I18n.asset.enums.ECorporationSteps[ESteps.CORPORATION_INFO],
				isActive: isStep([ESteps.CORPORATION_INFO]),
				onClick: () => setStep(ESteps.CORPORATION_INFO),
				isCompleted: completedSteps.includes(ESteps.CORPORATION_INFO),
			},
			{
				title: I18n.asset.enums.ECorporationSteps[ESteps.LEGAL_REPRESENTATIVE],
				isActive: isStep([ESteps.LEGAL_REPRESENTATIVE]),
				onClick: () => setStep(ESteps.LEGAL_REPRESENTATIVE),
				isCompleted: completedSteps.includes(ESteps.LEGAL_REPRESENTATIVE),
			},
			{
				title: I18n.asset.enums.ECorporationSteps[ESteps.BENEFICIARIES],
				isActive: isStep([ESteps.BENEFICIARIES]),
				onClick: () => setStep(ESteps.BENEFICIARIES),
				isCompleted: completedSteps.includes(ESteps.BENEFICIARIES),
			},
		],
		[completedSteps, isStep, setStep],
	);
};

function hydrateCorporationInfo(formDataCorporationInfo: Partial<CorporationSheetResponseResource>, isEdit: boolean | undefined) {
	const data = {
		raisonSociale: formDataCorporationInfo.raisonSociale,
		siret: formDataCorporationInfo.siret,
		formeJuridique: formDataCorporationInfo.formeJuridique,
		formeJuridiqueAutre: formDataCorporationInfo.formeJuridiqueAutre,
		siegeSocial: {
			address: formDataCorporationInfo.siegeSocial?.address ?? "",
			zipCode: formDataCorporationInfo.siegeSocial?.zipCode ?? "",
			city: formDataCorporationInfo.siegeSocial?.city ?? "",
			country: formDataCorporationInfo.siegeSocial?.country ?? ECountry.france,
		},
		residenceFiscale: formDataCorporationInfo.residenceFiscale,
		residenceFiscaleAutre: formDataCorporationInfo.residenceFiscaleAutre,
		regimeFiscal: formDataCorporationInfo.regimeFiscal,
		assujettiIFI: formDataCorporationInfo.assujettiIFI,
	};

	if (isEdit) {
		return CorporationSheetUpdateRequestResource.hydratePartial<CorporationSheetUpdateRequestResource>(
			{
				...data,
				id: formDataCorporationInfo.id,
				corporationId: formDataCorporationInfo.corporationId,
			},
			{
				groups: isEdit ? groupsCorporationInfoEdit : groupsCorporationInfoCreate,
			},
		);
	}

	return CorporationSheetCreateRequestResource.hydratePartial<CorporationSheetCreateRequestResource>(data, {
		groups: isEdit ? groupsCorporationInfoEdit : groupsCorporationInfoCreate,
	});
}

function hydrateLegalRepresentative(formDataLegalRepresentative: Partial<CorporationSheetResponseResource>, isEdit: boolean | undefined) {
	const data = {
		qualiteRepresentantLegal: formDataLegalRepresentative.qualiteRepresentantLegal,
		representantLegal: formDataLegalRepresentative.representantLegal,
	};

	if (isEdit) {
		return CorporationSheetUpdateRequestResource.hydratePartial<CorporationSheetUpdateRequestResource>(data, {
			groups: groupsLegalRepresentative,
		});
	}

	return CorporationSheetCreateRequestResource.hydratePartial<CorporationSheetCreateRequestResource>(data, {
		groups: groupsLegalRepresentative,
	});
}
